/**
 * ChartLegend Component
 *
 * Horizontal legend for the Sales Timeline charts.
 * Shows strain colours and demand line indicator.
 *
 * US-PE-006: Sales Timeline - Phase 2 Chart Components
 */

/**
 * Legend component for WeekChart
 *
 * @param {Object} props
 * @param {Array} props.strains - Array of strain objects with id, name, color
 * @param {boolean} [props.showDemandLine=true] - Whether to show demand line in legend
 * @param {boolean} [props.compact=false] - Use compact layout for smaller containers
 * @param {string} [props.className] - Additional CSS classes
 */
export default function ChartLegend({
  strains = [],
  showDemandLine = true,
  compact = false,
  className = ''
}) {
  // Filter to only strains that have data (non-empty)
  const visibleStrains = strains.filter(s => s.name && s.color)

  if (visibleStrains.length === 0 && !showDemandLine) {
    return null
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${compact ? 'text-xs' : 'text-sm'} ${className}`}
      role="group"
      aria-label="Chart legend"
    >
      {/* Strain indicators */}
      {visibleStrains.map((strain) => (
        <div
          key={strain.id}
          className="flex items-center gap-1.5"
        >
          <div
            className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} rounded-sm flex-shrink-0`}
            style={{ backgroundColor: strain.color }}
            aria-hidden="true"
          />
          <span className="text-slate-600 whitespace-nowrap">
            {strain.name}
          </span>
        </div>
      ))}

      {/* Demand line indicator */}
      {showDemandLine && (
        <div className="flex items-center gap-1.5">
          <div className="flex items-center" aria-hidden="true">
            <div
              className={`${compact ? 'w-4' : 'w-5'} h-0.5 rounded`}
              style={{ backgroundColor: '#1e293b' }}
            />
            <div
              className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full -ml-1 border border-white`}
              style={{ backgroundColor: '#1e293b' }}
            />
          </div>
          <span className="text-slate-600 whitespace-nowrap">
            Demand
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Inline legend item for use within other components
 *
 * @param {Object} props
 * @param {string} props.color - Colour for the indicator
 * @param {string} props.label - Label text
 * @param {string} [props.type='square'] - Type of indicator: 'square' | 'line' | 'dot'
 */
export function LegendItem({ color, label, type = 'square' }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      {type === 'square' && (
        <div
          className="w-3 h-3 rounded-sm flex-shrink-0"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      {type === 'line' && (
        <div className="flex items-center" aria-hidden="true">
          <div
            className="w-5 h-0.5 rounded"
            style={{ backgroundColor: color }}
          />
          <div
            className="w-2 h-2 rounded-full -ml-1 border border-white"
            style={{ backgroundColor: color }}
          />
        </div>
      )}
      {type === 'dot' && (
        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      <span className="text-slate-600">{label}</span>
    </div>
  )
}
